/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { RootState } from '../../reducers';
import { hasAnyRole, allowedRolesForEnv } from '../authHelper';

/**
 * Did this browser ever hold a session?
 *
 * grantToken persists refreshToken/idToken on login and clears them on logout,
 * so their presence separates "my access token expired" from "I have never
 * signed in here" (a fresh or incognito profile). Only the former can possibly
 * be recovered by a silent prompt=none re-auth; for the latter it is guaranteed
 * to fail, so we must not make that user wait for it.
 */
const hadSession = () => {
  try {
    return !!(
      localStorage.getItem('refreshToken') || localStorage.getItem('idToken')
    );
  } catch {
    // Storage can throw in hardened/private modes; treat as "no session".
    return false;
  }
};

export const usePrivateRoute = () => {
  const auth = useSelector((state: RootState) => state.auth);
  // const appState = useSelector((state: RootState) => state.app);
  const location = useLocation();
  const navigate = useNavigate();
  // True while we still might recover a session in the background. The caller
  // must not render protected content during this window: the checks below run
  // in an effect (and the silent re-auth is async), so without this the whole
  // app shell — nav, "Hello, <user>", page content — painted for seconds before
  // the redirect to /login, including for users who had never signed in.
  const [resolving, setResolving] = useState(
    () => !auth.loggedIn && location.pathname !== '/login' && hadSession(),
  );

  useEffect(() => {
    // // 如果应用还在初始化中，不执行任何路由检查
    // if(appState.isInitializing) {
    //   return;
    // }

    // // 如果 appParam 还没有加载完成，不执行路由检查
    // if(!appState.appParam) {
    //   return;
    // }

    // // 检查是否为开发模式且配置了跳过鉴权
    // const isDevMode = appState.appParam.dev_mode === true;
    // const skipAuth = appState.appParam.skip_auth === true;

    // // 如果是开发模式且配置了跳过鉴权，不执行路由检查
    // if(isDevMode && skipAuth) {
    //   return;
    // }

    // 正常的登录检查逻辑
    if (!auth.loggedIn) {
      // Before bouncing to /login, try a silent re-auth against the (usually
      // still-alive) corporate SSO session. If it succeeds, grantToken sets
      // loggedIn=true and this effect re-runs without redirecting — the user
      // never sees the login page. Only if silent re-auth fails do we redirect.
      //
      // Skipped for a browser that has never signed in: prompt=none cannot
      // succeed without a prior session, so attempting it only delays the
      // redirect (by up to the iframe timeout) for no benefit.
      if (location.pathname !== '/login' && hadSession()) {
        let cancelled = false;
        (async () => {
          try {
            const { silentReauth } = await import('../silentReauth');
            const ok = await silentReauth();
            if (!ok && !cancelled) {
              setResolving(false);
              navigate('/login', { state: { from: location } });
            }
          } catch {
            if (!cancelled) {
              setResolving(false);
              navigate('/login', { state: { from: location } });
            }
          }
        })();
        return () => {
          cancelled = true;
        };
      }
      setResolving(false);
      navigate('/login', { state: { from: location } });
      return undefined;
    }
    setResolving(false);

    // 角色授权检查：根据 URL 环境判断允许访问的角色
    const allowedRoles = allowedRolesForEnv();
    const isAuthorized = hasAnyRole(auth.group, allowedRoles);
    const isOnNoPermission = location.pathname === '/no-permission';
    const isOnLogin = location.pathname === '/login';
    if (!isAuthorized && !isOnNoPermission && !isOnLogin) {
      navigate('/no-permission', { replace: true });
      return undefined;
    }
    return undefined;

  }, [
    auth.loggedIn,
    // appState.appParam,
    // appState.isInitializing
  ]);

  return {
    /** Safe to render protected content. */
    ready: auth.loggedIn,
    /** Auth outcome still unknown — render a placeholder, never the app. */
    resolving,
  };
};
